import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, Copy, Star, Search, X } from "lucide-react";

const API = getApiUrl();

/**
 * Browsable grid of icons saved via the Icon Generator — reused both
 * inside the Icon Generator tool itself and in the Media Library's
 * "Icons" tab, so there's a single source of truth for the gallery logic.
 * Pass a changing `refreshToken` to force a reload (e.g. after creating
 * a new icon elsewhere on the same page).
 */
export default function IconGalleryGrid({ refreshToken }) {
  const { styles: siteStyles, refetch: refetchSiteStyles } = useSiteStyles();
  const [icons, setIcons] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [settingFaviconId, setSettingFaviconId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const currentFaviconUrl = siteStyles?.faviconUrl || "";
  const filteredIcons = icons.filter((icon) =>
    !searchTerm || icon.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const loadGallery = useCallback(async () => {
    setLoadingGallery(true);
    try {
      const { data } = await axios.get(`${API}/admin/icons`, { withCredentials: true });
      setIcons(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load icon gallery");
    } finally {
      setLoadingGallery(false);
    }
  }, []);

  useEffect(() => { loadGallery(); }, [loadGallery, refreshToken]);

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(resolveImageUrl(url));
    toast.success("Image URL copied");
  };

  const handleSetFavicon = async (icon) => {
    setSettingFaviconId(icon.id);
    try {
      await axios.put(`${API}/admin/site-styles`, { styles: { faviconUrl: icon.image_url } }, { withCredentials: true });
      await refetchSiteStyles();
      toast.success(`"${icon.name}" set as the site favicon`);
    } catch {
      toast.error("Failed to set favicon");
    } finally {
      setSettingFaviconId(null);
    }
  };

  const handleResetFavicon = async () => {
    try {
      await axios.put(`${API}/admin/site-styles`, { styles: { faviconUrl: "" } }, { withCredentials: true });
      await refetchSiteStyles();
      toast.success("Favicon reset to default");
    } catch {
      toast.error("Failed to reset favicon");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/icons/${deleteTarget.id}`, { withCredentials: true });
      setIcons((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      if (deleteTarget.image_url === currentFaviconUrl) {
        await axios.put(`${API}/admin/site-styles`, { styles: { faviconUrl: "" } }, { withCredentials: true });
        await refetchSiteStyles();
        toast.success("Icon deleted — it was the site favicon, so that's been reset to default");
      } else {
        toast.success("Icon deleted");
      }
    } catch {
      toast.error("Failed to delete icon");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div data-testid="icon-gallery-section">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-sm">Icon Gallery {icons.length > 0 && `(${icons.length})`}</h4>
        {currentFaviconUrl && (
          <Button variant="ghost" size="sm" onClick={handleResetFavicon} data-testid="icon-generator-reset-favicon-btn">
            Reset to default favicon
          </Button>
        )}
      </div>
      {icons.length > 0 && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search icons by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10"
            data-testid="icon-gallery-search-input"
          />
          {searchTerm && (
            <button type="button" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="icon-gallery-search-clear">
              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      )}
      {loadingGallery ? (
        <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-muted-foreground" /></div>
      ) : icons.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No icons saved yet. Create one in the Icon Generator (System &gt; Tools) and save it to the gallery.</p>
      ) : filteredIcons.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No icons found matching "{searchTerm}".</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4" data-testid="icon-gallery-grid">
          {filteredIcons.map((icon) => {
            const isFavicon = icon.image_url === currentFaviconUrl;
            return (
              <div key={icon.id} className={`border p-2 flex flex-col items-center gap-2 ${isFavicon ? "border-amber-500 ring-1 ring-amber-500" : "border-border"}`} data-testid={`icon-gallery-item-${icon.id}`}>
                <img src={resolveImageUrl(icon.image_url)} alt={icon.name} className="w-16 h-16 object-contain" />
                <p className="text-xs font-medium truncate w-full text-center" title={icon.name}>{icon.name}</p>
                {isFavicon && <p className="text-[10px] text-amber-600 -mt-1">Current favicon</p>}
                <div className="flex gap-1">
                  <button type="button" onClick={() => handleCopyUrl(icon.image_url)} title="Copy image URL"
                    className="text-muted-foreground hover:text-foreground p-1" data-testid={`icon-gallery-copy-${icon.id}`}>
                    <Copy size={14} />
                  </button>
                  <button type="button" onClick={() => handleSetFavicon(icon)} disabled={settingFaviconId === icon.id} title="Set as site favicon"
                    className={`p-1 disabled:opacity-40 ${isFavicon ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`} data-testid={`icon-gallery-favicon-${icon.id}`}>
                    {settingFaviconId === icon.id ? <Loader2 size={14} className="animate-spin" /> : <Star size={14} fill={isFavicon ? "currentColor" : "none"} />}
                  </button>
                  <button type="button" onClick={() => setDeleteTarget(icon)} title="Delete"
                    className="text-destructive hover:text-destructive/80 p-1" data-testid={`icon-gallery-delete-${icon.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this icon?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be permanently removed from the gallery. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="icon-gallery-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="icon-gallery-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
